import type { ComponentType, CSSProperties } from 'react';
import { BarChart2, Code, Layout, TrendingUp, Type } from 'react-feather';
import type { DashboardWidgetType } from '@trinity/chat/types/dashboard-config';

type PaletteIcon = ComponentType<{ size?: number; color?: string; style?: CSSProperties }>;

/**
 * What the builder needs to know about a widget type without loading its editor. Kept apart from
 * `widget-editors/` on purpose: the palette is imported by the read-only canvas, and editors pull in
 * every config form.
 */
export interface WidgetBuilderInfo {
  /** The tile in the management builder's palette; omitted for types it doesn't add directly. */
  palette?: { label: string; icon: PaletteIcon };
  /** Whether a data connection can feed this type (its form offers a remote source). */
  remoteCapable: boolean;
}

/**
 * Builder info for every widget type; a new widget type is a type error here until it is added.
 * The order is the palette's tile order. Library-backed builders (segments, patient journey) list
 * their own tiles in `PaletteContext.tsx`.
 */
export const WIDGET_BUILDER_INFO: Record<DashboardWidgetType, WidgetBuilderInfo> = {
  chart: { palette: { label: 'Charts', icon: BarChart2 }, remoteCapable: true },
  text: { palette: { label: 'Rich Text', icon: Type }, remoteCapable: true },
  metric_card: { palette: { label: 'Metric Card', icon: TrendingUp }, remoteCapable: true },
  code: { palette: { label: 'Code', icon: Code }, remoteCapable: true },
  rep_dashboard: { palette: { label: 'Territory-Level Insights', icon: Layout }, remoteCapable: true },
  quote: { remoteCapable: false },
  segment_overview: { remoteCapable: false },
  library_widget: { remoteCapable: false },
  pj_media: { remoteCapable: false },
  seg_widget: { remoteCapable: false },
  pj_widget: { remoteCapable: false },
};
